import { createContext, useContext, useMemo, useRef, useState } from 'react'
import { AccessibilityInfo } from 'react-native'

import { ToastContainer } from './ToastContainer'

type ShowParams = {
  heading: string
  description?: string
  hideAfter?: number
  element?: React.ReactElement
  intent: string
  action?: object
  leftIcon?: string
}

type ToastContextType = {
  show: (params: ShowParams) => void
  hide: () => void
  isVisible: boolean
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider = ({ children }: any) => {
  const [show, setShow] = useState(false)
  const [heading, setHeading] = useState<string | undefined>()
  const [description, setDescription] = useState<string | undefined>()
  const [intent, setIntent] = useState<string | undefined>()
  const [render, setRender] = useState<JSX.Element | null>()
  const [action, setAction] = useState<object | undefined>()
  const [leftIcon, setLeftIcon] = useState<string | undefined>()
  const hideTimeoutRef = useRef<any>(null)

  const value = useMemo(
    () => ({
      show: ({
        heading,
        description,
        hideAfter,
        element,
        intent,
        action,
      }: ShowParams) => {
        setRender(element)
        setDescription(description)
        setHeading(heading)
        setIntent(intent)
        setAction(action)
        setShow(true)

        if (description) {
          AccessibilityInfo.announceForAccessibility(description)
        }

        if (hideAfter) {
          hideTimeoutRef.current = setTimeout(() => {
            setShow(false)
          }, hideAfter)
        }
        if (leftIcon) {
          setLeftIcon(leftIcon)
        }
      },
      hide: () => {
        setShow(false)
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
        }
      },
      isVisible: show,
    }),
    [show, setShow]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer
        intent={intent}
        show={show}
        hide={value.hide}
        heading={heading}
        description={description}
        render={render}
        action={action}
        leftIcon={leftIcon}
      />
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const toast = useContext(ToastContext)
  if (toast === null) {
    console.error('Trying to use useToast without a ToastProvider')
  }

  return toast
}
